import { useMemo, useState } from 'react';
import { Stack, Text, Loader, Center, Alert } from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import type { DatesRangeValue } from '@mantine/dates';
import type { TaskType, TaskCompletion } from '@/types/task';
import {
  useTaskCompletions,
  useUpdateTaskCompletion,
  useDeleteTaskCompletion,
} from '@/hooks/useTasks';
import { TaskTypeFilter } from './TaskTypeFilter';
import { TaskCompletionTable } from './TaskCompletionTable';
import { EditCompletionModal } from './EditCompletionModal';

export function TaskCompletionHistory() {
  // Date range state (default: last 90 days)
  const [dateRange, _setDateRange] = useState<DatesRangeValue>(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 90);
    return [start, end];
  });

  // Task type filter state (empty = all selected)
  const [selectedTypes, setSelectedTypes] = useState<TaskType[]>([]);

  // Edit modal state
  const [editingCompletion, setEditingCompletion] = useState<TaskCompletion | null>(null);

  // Fetch completions based on date range
  const completionFilters = useMemo(() => {
    if (!dateRange[0] || !dateRange[1]) return {};
    const start = dateRange[0] instanceof Date ? dateRange[0].toISOString() : dateRange[0];
    const end = dateRange[1] instanceof Date ? dateRange[1].toISOString() : dateRange[1];
    return { start_date: start, end_date: end };
  }, [dateRange]);

  const { data: completionsData, isLoading, error } = useTaskCompletions(completionFilters);

  // Mutations
  const updateMutation = useUpdateTaskCompletion();
  const deleteMutation = useDeleteTaskCompletion();

  if (error) {
    return (
      <Alert icon={<IconAlertCircle size={16} />} color="red">
        {error instanceof Error ? error.message : 'Failed to load completion history'}
      </Alert>
    );
  }

  if (isLoading) {
    return (
      <Center py="xl">
        <Stack align="center" gap="md">
          <Loader size="lg" />
          <Text c="dimmed">Loading history...</Text>
        </Stack>
      </Center>
    );
  }

  // Filter completions by task type
  const filteredCompletions = (() => {
    if (!completionsData?.completions) return [];
    if (selectedTypes.length === 0) return completionsData.completions;
    return completionsData.completions.filter((c) =>
      c.task_type ? selectedTypes.includes(c.task_type) : false
    );
  })();

  const handleCompletionClick = (completion: TaskCompletion) => {
    setEditingCompletion(completion);
  };

  const handleSaveCompletion = (completionId: string, notes: string, completedAt: string) => {
    updateMutation.mutate(
      { completionId, data: { notes, completed_at: completedAt } },
      { onSuccess: () => setEditingCompletion(null) }
    );
  };

  const handleDeleteCompletion = (completionId: string) => {
    deleteMutation.mutate(completionId, {
      onSuccess: () => setEditingCompletion(null),
    });
  };

  return (
    <Stack gap="lg">
      <Stack gap="md">
        <Text size="lg" fw={600}>
          Completion History
        </Text>

        <TaskTypeFilter
          completions={completionsData?.completions ?? []}
          selectedTypes={selectedTypes}
          onSelectionChange={setSelectedTypes}
        />

        <TaskCompletionTable
          completions={filteredCompletions}
          onRowClick={handleCompletionClick}
        />
      </Stack>

      <EditCompletionModal
        opened={!!editingCompletion}
        onClose={() => setEditingCompletion(null)}
        completion={editingCompletion}
        onSave={handleSaveCompletion}
        onDelete={handleDeleteCompletion}
        isLoading={updateMutation.isPending || deleteMutation.isPending}
      />
    </Stack>
  );
}
